import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { type StripeElementsOptions, loadStripe } from '@stripe/stripe-js';
import {
  type MakePaymentInput,
  PaymentMethod,
  PaymentStatus,
  isProd,
  queryClient,
  resolveBackendError,
} from '@tyro/api';
import {
  Checkbox,
  LoadingPlaceholder,
  toast,
  usePreferredNameLayout,
} from '@tyro/core';
import { Trans, useFormatNumber, useTranslation } from '@tyro/i18n';
import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPayment } from '../../../api/create-payment';
import { feeKeys } from '../../../api/keys';
import { useServiceCharge } from '../../../api/service-charge';
import { getSetupIntentSecret } from '../../../api/setup-intent';
import { type PaymentsToPayAndMethod, usePayFeesSettings } from './store';

const stripeKey = isProd
  ? 'pk_live_51MWMNZDT811pK8VEu73qKj1NacjbFiZ3lqpIQTnrAViF6zz69eV9Tz9unuRiDBe5hctXIw3ju5AtmaZ8AkVdAIXV00112GPYBe'
  : 'pk_test_51MWMNZDT811pK8VExdSBnjE9DgKkb7FO9yxEIBv5MHYPHoHDO9fw78GI0Leb2EPEHzJGvXxU1IPiHAXK1YqRJYzj003FYTtroQ';
const stripePromise = loadStripe(stripeKey, {
  betas: ['elements_customers_beta_1'],
});

interface PayFeesModalProps {
  paymentInput: MakePaymentInput;
  saveCard?: boolean;
  setSaveCard?: (checked: boolean) => void;
}

interface PaymentPlanCardCheckoutFormProps extends PayFeesModalProps {
  recurringPayments: PaymentsToPayAndMethod['fees'];
}

function PaymentPlanCardCheckoutForm({
  paymentInput,
  recurringPayments,
}: PaymentPlanCardCheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation(['fees']);
  const { setNextAction, onClose, setDisableConfirm, setIsSubmitting } =
    usePayFeesSettings();
  const { formatCurrency } = useFormatNumber();

  const amounts = useMemo(
    () => ({
      total: recurringPayments.reduce(
        (acc, fee) =>
          acc +
          (fee.paymentPlanInstallments?.[0]?.amount ?? 0) +
          (fee.paymentPlanInstallments?.[0]?.serviceCharges ?? 0),
        0,
      ),
      remaining: recurringPayments.reduce((acc, fee) => {
        const [_, ...remainingInstallments] = fee.paymentPlanInstallments ?? [];
        return (
          acc +
          remainingInstallments.reduce(
            (acc, installment) =>
              acc +
              (installment?.amount ?? 0) +
              (installment?.serviceCharges ?? 0),
            0,
          )
        );
      }, 0),
    }),
    recurringPayments,
  );

  const sanitizedLink = DOMPurify.sanitize(t('fees:terms'), {
    ALLOWED_TAGS: ['a'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      return;
    }

    try {
      setIsSubmitting(true);
      const { fees_createSetupIntent: feesCreateSetupIntent } =
        await getSetupIntentSecret({
          onBehalfOf: paymentInput.onBehalfOfEmail,
          partyId: paymentInput.onBehalfOfPartyId,
        });

      const redirectUrl = new URL(window.location.href);
      redirectUrl.searchParams.set('paymentStatus', 'succeeded');

      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret: feesCreateSetupIntent.clientSecret,
        confirmParams: {
          return_url: redirectUrl.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message);
        setIsSubmitting(false);
      } else {
        const { fees_createPayment: feesCreatePayment } = await createPayment({
          ...paymentInput,
          saveCard: true,
          stripePaymentMethodId: setupIntent?.payment_method as string,
        });

        if (!feesCreatePayment) {
          toast.error(t('fees:paymentUnsuccessful'));
          return;
        }

        setTimeout(async () => {
          await queryClient.invalidateQueries({ queryKey: feeKeys.all });
          toast.success(t('fees:paymentSuccessful'));
          onClose();
          setIsSubmitting(false);
        }, 5000);
      }
    } catch (error) {
      const description = await resolveBackendError(error);

      toast.error(t('fees:paymentUnsuccessful'), { description });
      setIsSubmitting(false);
    }
  }, [stripe, elements, paymentInput, setIsSubmitting]);

  useEffect(() => {
    setDisableConfirm(!stripe || !elements);
  }, [stripe, elements]);

  useEffect(() => {
    setNextAction(() => handleSubmit);
  }, [setNextAction, handleSubmit]);

  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="subtitle1" color="text.secondary">
          {t('fees:totalPayingToday')}
        </Typography>
        <Typography variant="h5">
          {formatCurrency(amounts.total ?? 0)}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          {t('fees:remainingBalance')}: {formatCurrency(amounts.remaining ?? 0)}
        </Typography>
      </Stack>
      <Divider />
      <Stack spacing={2} pb={0.25}>
        <Tooltip
          title={t('fees:disabledSaveCardTooltip')}
          placement="top-start"
        >
          <span>
            <Checkbox
              label={t('fees:saveCardToYourAccount')}
              checkboxProps={{
                checked: true,
                disabled: true,
              }}
            />
          </span>
        </Tooltip>
        <PaymentElement options={{ terms: { card: 'never' } }} />
        <Box
          component="span"
          sx={{
            fontSize: '14.88px',
            color: 'gray.500',
            '& a': {
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            },
          }}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
          dangerouslySetInnerHTML={{ __html: sanitizedLink }}
        />
      </Stack>
    </Stack>
  );
}

function OneTimeCardCheckoutForm({
  paymentInput,
  saveCard,
  setSaveCard,
}: PayFeesModalProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation(['fees']);
  const { setNextAction, onClose, setDisableConfirm, setIsSubmitting } =
    usePayFeesSettings();
  const { formatCurrency } = useFormatNumber();

  const { data: serviceCharge } = useServiceCharge({
    charges:
      paymentInput?.instantPayments?.map(({ feeId, amount }) => ({
        feeId,
        amount,
      })) ?? [],
  });

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      return;
    }

    try {
      setIsSubmitting(true);
      const { fees_createPayment: feesCreatePayment } = await createPayment({
        ...paymentInput,
        saveCard,
      });

      if (!feesCreatePayment) {
        toast.error(t('fees:paymentUnsuccessful'));
        return;
      }

      const redirectUrl = new URL(window.location.href);
      redirectUrl.searchParams.set('paymentStatus', 'succeeded');

      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret: feesCreatePayment.clientSecret,
        confirmParams: {
          return_url: redirectUrl.href,
          save_payment_method: saveCard,
        },
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message);
        setIsSubmitting(false);
      } else {
        setTimeout(async () => {
          await queryClient.invalidateQueries({ queryKey: feeKeys.all });
          toast.success(t('fees:paymentSuccessful'));
          onClose();
          setIsSubmitting(false);
        }, 5000);
      }
    } catch (error) {
      const description = await resolveBackendError(error);

      toast.error(t('fees:paymentUnsuccessful'), { description });
      setIsSubmitting(false);
    }
  }, [stripe, elements, paymentInput, setIsSubmitting, saveCard]);

  useEffect(() => {
    setDisableConfirm(!stripe || !elements);
  }, [stripe, elements]);

  useEffect(() => {
    setNextAction(() => handleSubmit);
  }, [setNextAction, handleSubmit]);

  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="subtitle1" color="text.secondary">
          {t('fees:totalPayingToday')}
        </Typography>
        <Typography variant="h5">
          {formatCurrency(serviceCharge?.amount ?? 0)}
        </Typography>
      </Stack>
      <Divider />
      <Stack spacing={2} pb={0.25}>
        <span>
          <Checkbox
            label={t('fees:saveCardToYourAccount')}
            checkboxProps={{
              checked: saveCard,
              onChange: (_, checked) => setSaveCard?.(checked),
            }}
          />
        </span>
        <PaymentElement />
      </Stack>
    </Stack>
  );
}

function CashCheckoutForm({ paymentInput }: PayFeesModalProps) {
  const { t } = useTranslation(['common', 'fees']);
  const { setNextAction, paymentsToPayAndMethod, onClose, setIsSubmitting } =
    usePayFeesSettings();
  const { displayName } = usePreferredNameLayout();
  const { formatCurrency } = useFormatNumber();

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await createPayment(paymentInput);

      setTimeout(async () => {
        await queryClient.invalidateQueries({ queryKey: feeKeys.all });
        toast.success(t('fees:paymentSuccessful'));
        onClose();
        setIsSubmitting(false);
      }, 5000);
    } catch (error) {
      setIsSubmitting(false);
      const description = await resolveBackendError(error);

      toast.error(t('fees:paymentUnsuccessful'), { description });
    }
  }, [paymentInput]);

  useEffect(() => {
    setNextAction(() => handleSubmit);
  }, [setNextAction, handleSubmit]);

  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="subtitle1" color="text.secondary">
          {t('fees:totalPayingToday')}
        </Typography>
        <Typography variant="h5">
          {formatCurrency(paymentsToPayAndMethod?.total ?? 0)}
        </Typography>
      </Stack>
      <Stack
        sx={{
          backgroundColor: 'slate.100',
          borderRadius: 2,
          p: 1.5,
        }}
        spacing={2}
      >
        <Typography variant="body1" color="slate.500">
          {t('common:summary')}
        </Typography>
        <Stack>
          {paymentsToPayAndMethod?.fees.map((fee) => {
            const { feeName, person } = fee;
            const studentName = displayName(person);
            return (
              <Stack
                key={JSON.stringify(fee.id)}
                direction="row"
                justifyContent="space-between"
              >
                <Typography
                  variant="subtitle1"
                  component="span"
                  sx={{
                    lineHeight: 1.2,
                  }}
                >
                  <Trans ns="fees" i18nKey="feeNameForStudent">
                    {{ feeName }}
                    <br />
                    <Typography
                      variant="body2"
                      component="span"
                      color="text.secondary"
                    >
                      <>for {{ studentName }}</>
                    </Typography>
                  </Trans>
                </Typography>

                {fee.amountToPay !== fee.amount ? (
                  <Typography>
                    {formatCurrency(fee.amountToPay)}{' '}
                    {t('fees:ofFeeAmount', {
                      feeAmount: formatCurrency(fee.amount - fee.amountPaid),
                    })}
                  </Typography>
                ) : (
                  <Typography>{formatCurrency(fee.amountToPay)}</Typography>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Stack>
  );
}

export function PayFeesStepTwo() {
  const { t } = useTranslation(['fees']);
  const { paymentsToPayAndMethod, customerFeesInfo, isCustomerInfoLoading } =
    usePayFeesSettings();
  const [saveCard, setSaveCard] = useState(false);

  const oneTimePaymentInput = useMemo<MakePaymentInput>(
    () => ({
      instantPayments:
        paymentsToPayAndMethod?.fees.map(({ id, amountToPay, person }) => ({
          feeId: id.feeId,
          amount: amountToPay,
          studentPartyId: person.partyId,
          note: paymentsToPayAndMethod.details,
        })) ?? [],
      paymentMethod:
        paymentsToPayAndMethod?.paymentMethod ?? PaymentMethod.Card,
      paymentStatus: PaymentStatus.Created,
      onBehalfOfEmail:
        paymentsToPayAndMethod?.onBehalfOf?.personalInformation?.primaryEmail
          ?.email,
      onBehalfOfPartyId: paymentsToPayAndMethod?.onBehalfOf?.partyId,
    }),
    [paymentsToPayAndMethod],
  );

  const paymentPlanProps = useMemo<PaymentPlanCardCheckoutFormProps>(() => {
    return {
      paymentInput: {
        recurringPayments:
          paymentsToPayAndMethod?.fees?.map(({ id, person }) => ({
            feeId: id.feeId,
            studentPartyId: person.partyId,
            note: paymentsToPayAndMethod.details,
          })) ?? [],
        paymentMethod:
          paymentsToPayAndMethod?.paymentMethod ?? PaymentMethod.Card,
        paymentStatus: PaymentStatus.Created,
        onBehalfOfEmail:
          paymentsToPayAndMethod?.onBehalfOf?.personalInformation?.primaryEmail
            ?.email,
        onBehalfOfPartyId: paymentsToPayAndMethod?.onBehalfOf?.partyId,
      },
      recurringPayments: paymentsToPayAndMethod?.fees ?? [],
    };
  }, [paymentsToPayAndMethod]);

  const options = useMemo<StripeElementsOptions>(
    () =>
      ({
        currency: 'eur',
        customerOptions: {
          customer: customerFeesInfo?.customerId ?? '',
          ephemeralKey: customerFeesInfo?.ephemeralKey ?? '',
        },
        ...(paymentsToPayAndMethod?.cardPaymentType === 'payment-plan'
          ? {
              mode: 'setup',
            }
          : {
              mode: 'payment',
              amount: Math.round((paymentsToPayAndMethod?.total ?? 0) * 100),
              setup_future_usage: saveCard ? 'off_session' : undefined,
            }),
      }) as const,
    [paymentsToPayAndMethod, saveCard, customerFeesInfo],
  );

  if (!paymentsToPayAndMethod || isCustomerInfoLoading) {
    return <LoadingPlaceholder sx={{ minHeight: 200, position: 'relative' }} />;
  }

  if (
    !paymentsToPayAndMethod ||
    (paymentsToPayAndMethod.paymentMethod === PaymentMethod.Card &&
      !customerFeesInfo)
  ) {
    return <Alert severity="error">{t('fees:issueWithContactSetup')}</Alert>;
  }

  if (paymentsToPayAndMethod.paymentMethod !== PaymentMethod.Card) {
    return <CashCheckoutForm paymentInput={oneTimePaymentInput} />;
  }

  if (paymentsToPayAndMethod.cardPaymentType === 'payment-plan') {
    return (
      <Elements stripe={stripePromise} options={options}>
        <PaymentPlanCardCheckoutForm {...paymentPlanProps} />
      </Elements>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <OneTimeCardCheckoutForm
        paymentInput={oneTimePaymentInput}
        saveCard={saveCard}
        setSaveCard={setSaveCard}
      />
    </Elements>
  );
}
